import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import CompositeCbctDialog from '~/containers/workflows/composite-cbct/CompositeCbctDialog';
import { Modality } from '~/domain/dicom/modality';
import usePrimaryImage from '~/hooks/image/usePrimaryImage';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { WorkflowTypes } from '~/state/atoms/viewerAtoms';
import AppConfig from '~/utils/appconfig';
import AiAssistantButton from '../../AiAssistantButton';
import DatasetPicker from '../ContouringStep/DatasetPicker/DatasetPicker';
import StepHeader from '../StepHeader';

export default function SyntheticImagingContent() {
    const { availableImages } = usePrimaryImage(true);
    const selectedPatient = useSelectedPatient();
    const currentPrimaryImage = useSelectedPrimaryImage();
    const [isCompositeCbctOpen, setIsCompositeCbctOpen] = useState(false);

    const eligibleCompositeCbctImageCount = useMemo(
        () =>
            availableImages.filter((img) => {
                if (img.modality !== Modality.CT || img.needsReformat) {
                    return false;
                }
                if (img.imageObject) {
                    return true;
                }
                // Session-only CT entries (no `imageObject`, advertised
                // as unloaded) are picked up by the dialog in connector
                // mode — the connector already holds the DICOM. Mirror
                // the dialog's `isEligible` so the button enables in
                // Eclipse / `/session` flows where only the primary is
                // loaded up front.
                return AppConfig.isConnectorMode && img.isLoaded === false;
            }).length,
        [availableImages]
    );

    const isCompositeCbctDisabled =
        !selectedPatient ||
        !currentPrimaryImage ||
        eligibleCompositeCbctImageCount < 2;
    const compositeCbctTooltipId =
        !selectedPatient || !currentPrimaryImage ? 'loadImageFirst' : undefined;

    return (
        <Box display="flex" flexDirection="column" height="100%">
            <Box flex={1} overflow="auto" minHeight={0}>
                <StepHeader titleId="workflow.syntheticImaging" />

                <Box
                    px={2}
                    my={1}
                    mb={1.5}
                    display="flex"
                    flexDirection="column"
                    gap={1}
                >
                    <AiAssistantButton page={WorkflowTypes.IMAGE} />
                    <Tooltip
                        title={
                            isCompositeCbctDisabled ? (
                                compositeCbctTooltipId ? (
                                    <FormattedMessage
                                        id={compositeCbctTooltipId}
                                    />
                                ) : (
                                    <FormattedMessage id="compositeCbctEligibleImagesRequired" />
                                )
                            ) : (
                                ''
                            )
                        }
                        placement="right"
                    >
                        <Box component="span">
                            <Button
                                fullWidth
                                variant="outlined"
                                disabled={isCompositeCbctDisabled}
                                onClick={() => setIsCompositeCbctOpen(true)}
                                data-testid="composite-cbct-button"
                                sx={{
                                    textTransform: 'capitalize',
                                    fontSize: 12,
                                }}
                            >
                                <FormattedMessage id="compositeCbct" />
                            </Button>
                        </Box>
                    </Tooltip>
                </Box>
                <Box px={2}>
                    <DatasetPicker />
                </Box>
                <CompositeCbctDialog
                    open={isCompositeCbctOpen}
                    onClose={() => setIsCompositeCbctOpen(false)}
                />

                {availableImages.length === 0 && (
                    <Box display="flex" px={2} pt={1}>
                        <Typography color="text.secondary" fontSize={12}>
                            <FormattedMessage id="noImagesAvailable" />
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
